import type {
  Action, BooleanRule, IndexedRule, PublicId, Rules,
} from 'flow/store/engine/types';
import type {
  GroupRule, ThresholdRule, RuleBuilderRule, RulePath,
  RuleInsertionPosition,
} from './types';
import { groupType, ruleInsertionPosition, ruleType } from './types';

/*
* Narrows the type definition to include only the rules that can be built into
* the reselect rules tree
*/
export function isRuleObject(rules: Rules): rules is (IndexedRule | BooleanRule) {
  if (typeof rules === 'string' || Array.isArray(rules)) {
    return false;
  }

  if (Object.keys(rules).length === 0) {
    return false;
  }

  return true;
}

export function isBooleanRule(rules: Rules): rules is BooleanRule {
  if (isRuleObject(rules)) {
    return (Object.hasOwn(rules, groupType.and) || Object.hasOwn(rules, groupType.or));
  }

  return false;
}

export function isIndexedRule(rules: Rules): rules is IndexedRule {
  if (isRuleObject(rules)) {
    return Object.hasOwn(rules, 'id');
  }

  return false;
}

export function isThresholdRule(rule: RuleBuilderRule): rule is ThresholdRule {
  return rule.type === ruleType.threshold;
}

export function isGroupRule(rule: RuleBuilderRule): rule is GroupRule {
  return rule.type === ruleType.group;
}

export function isAmbiguousGroup(rule: RuleBuilderRule) {
  return isGroupRule(rule) && rule.data.isAmbiguous;
}

export function getPathToParent(rulePath: RulePath) {
  return rulePath.slice(0, -1);
}

/** returns the last leg of rule path that paths from parent -> rule */
export function getParentToChildRuleSegment(rulePath: RulePath) {
  return rulePath[rulePath.length - 1];
}

/**
 * Returns the local index of a given rulepath - ie. where the rule lives
 * in its immediate parent. Todo: adjust with threshold root
 */
export function getLocalizedIndex(rulePath: RulePath) {
  return getParentToChildRuleSegment(rulePath).index;
}

export function shouldInsertIntoRelativeRule(insertPosition: RuleInsertionPosition) {
  return (
    [ruleInsertionPosition.firstChild, ruleInsertionPosition.lastChild]
      .includes(insertPosition)
  );
}

export function isTreeRoot(rule: RuleBuilderRule) {
  return rule.data.path.length === 0;
}

/** Determines whether or not the rule is the first child of its parent */
export function isFirstChild(rule: RuleBuilderRule) {
  return getParentToChildRuleSegment(rule.data.path).index === 0;
}

export function getRuleIdSet({ rules }: Action) {
  const ruleSet = new Set<PublicId>();

  function traverse(rule: BooleanRule | IndexedRule) {
    if (isIndexedRule(rule)) {
      ruleSet.add(rule.id);
      return;
    }
    ruleSet.add(rule.publicId);
    if (rule.and || rule.or) {
      const children = rule.and || rule.or;
      children.forEach(child => {
        if (isBooleanRule(child) || isIndexedRule(child)) {
          traverse(child);
        }
      });
    }
  }

  // will always be true
  if (isBooleanRule(rules)) {
    traverse(rules);
  }

  return ruleSet;
}
